import { SelectOption } from '@globaleris/ui.library'
import { EnumCommodity } from '../../api'
import {
  apiCreateConceptPrice,
  apiDeleteConceptPrice,
  apiDeleteLinkRelationalConcept,
  apiFetchConceptTable,
  apiFetchConceptTypesByClientType,
  apiFetchIVATypes,
  apiFetchLayoutConfiguration,
  apiFetchQuantityTypeOptions,
  apiGetAggregatorConcepts,
  apiGetBaseConceptByID,
  apiGetConceptByID,
  apiGetNonAggregatorConcepts,
  apiGetRelatedConcepts,
  apiLinkRelationalConcept,
  apiPatchConceptPrice,
  apiPostConcept,
  apiUpdateConcept,
  apiUpdateLinkRelationalConcept
} from '../../api/Argentia/endpoints/concepts/concepts.api'
import {
  Concept,
  ConceptModelRequest,
  ConceptPatchRequest,
  ConceptPriceModel,
  ConceptPriceModelRequest,
  ConceptPricePatchRequest
} from '../../api/Argentia/endpoints/concepts/model/concepts'
import {
  apiCreateConceptModified,
  apiDeleteConceptModified,
  apiGetConceptsModifiedByContractProductID,
  apiUpdateConceptModified
} from '../../api/Argentia/endpoints/conceptsModified/conceptsModified.api'
import { ConceptModifiedModel } from '../../api/Argentia/endpoints/conceptsModified/model/conceptsModified'
import { fetchPriceByID, fetchPriceValues } from '../../api/Argentia/endpoints/pricing/pricing.api'
import { PriceValuesModel } from '../../api/Argentia/endpoints/pricing/model/values'
import { EnumClientTypes } from '../../api/Argentia/enums/enumClientTypes'
import { EnumConceptType } from '../../api/Argentia/enums/enumConceptType'
import { enumEnumToValueNumber } from '../../helpers'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import { getActiveCompany } from '../../redux'
import { ConceptModifiedPatchRequestModel, ConceptModifiedRequestModel } from './concepts.models'
import { apiFetchProductConceptPriceById } from '../../api/Argentia/endpoints/products/products.api'
import { PricesModel } from '../../api/Argentia/endpoints/pricing/model/prices'

export const fetchConcepts = async ({
  clientType,
  commodity,
  includeInactives,
  lgBypass
}: {
  clientType: EnumClientTypes
  commodity: EnumCommodity
  includeInactives?: boolean | null
  lgBypass?: boolean | null
}): Promise<ServiceResponse<Concept[] | undefined>> => {
  const clientTypeNumber = enumEnumToValueNumber(EnumClientTypes, clientType)!
  const commodityNumber = enumEnumToValueNumber(EnumCommodity, commodity)!
  const activeCompany = getActiveCompany()

  return await apiFetchConceptTable({
    clientType: clientTypeNumber,
    commodity: commodityNumber,
    companyId: activeCompany?.id,
    includeInactives,
    lgBypass
  })
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchConceptTypesByClientType = async (
  clientType: EnumClientTypes
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFetchConceptTypesByClientType(clientType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchIVATypes = async (
  clientType: EnumClientTypes,
  conceptType: EnumConceptType | undefined
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFetchIVATypes(clientType, conceptType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getConceptByID = async (id: number) => {
  return await apiGetConceptByID(id)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getBaseConceptByID = async (id: number) => {
  return await apiGetBaseConceptByID(id)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createConcept = async (concept: ConceptModelRequest) => {
  return await apiPostConcept(concept)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateConcept = async (concept: ConceptModelRequest | ConceptPatchRequest) => {
  return await apiUpdateConcept(concept)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getConfigurationLayout = async (calculationType: number) => {
  return await apiFetchLayoutConfiguration(calculationType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getQuantityTypeOptions = async (calculationType: number) => {
  return await apiFetchQuantityTypeOptions(calculationType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createConceptPrice = async (
  conceptPriceModel: ConceptPriceModelRequest,
  version: string = '2.0'
) => {
  return await apiCreateConceptPrice(conceptPriceModel, version)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteConceptPrice = async (conceptPriceId: number, version: string = '2.0') => {
  return await apiDeleteConceptPrice(conceptPriceId, version)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateConceptPrice = async (
  conceptId: number,
  conceptPriceId: number,
  conceptPricePatch: ConceptPricePatchRequest,
  version: string = '2.0'
) => {
  return await apiPatchConceptPrice(conceptId, conceptPriceId, conceptPricePatch, version)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// ========== CONCEPT MODIFIED SERVICES ==========

export const getConceptsModifiedByContractProductID = async (
  contractProductID: number
): Promise<ServiceResponse<ConceptModifiedModel[] | undefined>> => {
  return await apiGetConceptsModifiedByContractProductID(contractProductID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createConceptModified = async (
  request: ConceptModifiedRequestModel
): Promise<ServiceResponse<ConceptModifiedModel | undefined>> => {
  return await apiCreateConceptModified(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateConceptModified = async (
  request: ConceptModifiedPatchRequestModel
): Promise<ServiceResponse<ConceptModifiedModel | undefined>> => {
  return await apiUpdateConceptModified(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteConceptModified = async (
  conceptID: number
): Promise<ServiceResponse<ConceptModifiedModel | undefined>> => {
  return await apiDeleteConceptModified(conceptID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getConceptPriceValues = async (
  priceId: number
): Promise<ServiceResponse<PriceValuesModel[] | undefined>> => {
  return await fetchPriceValues(priceId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getPriceByID = async (
  priceId: number
): Promise<ServiceResponse<PricesModel | undefined>> => {
  return await fetchPriceByID(priceId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getRelatedConcepts = async (
  conceptId: number
): Promise<ServiceResponse<Concept[] | undefined>> => {
  return await apiGetRelatedConcepts(conceptId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const linkRelationalConcept = async (request: {
  conceptId: number
  relationalConceptId: number
}): Promise<ServiceResponse<any>> => {
  return await apiLinkRelationalConcept(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteLinkRelationalConcept = async (request: {
  conceptId: number
  relationalConceptId: number
}): Promise<ServiceResponse<any>> => {
  return await apiDeleteLinkRelationalConcept(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateLinkRelationalConcept = async (request: {
  conceptId: number
  relationalConceptId: number
  ivaTypeConceptId: number
}): Promise<ServiceResponse<any>> => {
  return await apiUpdateLinkRelationalConcept(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getAggregatorConcepts = async ({
  clientType,
  commodity,
  includeInactives
}: {
  clientType: EnumClientTypes
  commodity: EnumCommodity
  includeInactives?: boolean | null
}): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  const clientTypeNumber = enumEnumToValueNumber(EnumClientTypes, clientType)!
  const commodityNumber = enumEnumToValueNumber(EnumCommodity, commodity)!
  const activeCompany = getActiveCompany()

  return await apiGetAggregatorConcepts({
    clientType: clientTypeNumber,
    commodity: commodityNumber,
    companyId: activeCompany?.id,
    includeInactives
  })
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getNonAggregatorConcepts = async ({
  clientType,
  commodity,
  includeInactives
}: {
  clientType: EnumClientTypes
  commodity: EnumCommodity
  includeInactives?: boolean | null
}): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  const clientTypeNumber = enumEnumToValueNumber(EnumClientTypes, clientType)!
  const commodityNumber = enumEnumToValueNumber(EnumCommodity, commodity)!
  const activeCompany = getActiveCompany()

  return await apiGetNonAggregatorConcepts({
    clientType: clientTypeNumber,
    commodity: commodityNumber,
    companyId: activeCompany?.id,
    includeInactives
  })
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
